/**
 * Signing in. The only screen that exists before the household has an identity.
 */

import { useState } from 'preact/hooks';

import { CognitoError, type Cognito } from '../auth/cognito';

interface Props {
  cognito: Cognito;
  onSignedIn: () => void;
}

export function SignIn({ cognito, onSignedIn }: Props) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [refusal, setRefusal] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  const submit = async (): Promise<void> => {
    setWorking(true);
    setRefusal(null);

    try {
      await cognito.signIn(email.trim(), password);
      onSignedIn();
    } catch (error) {
      setRefusal(
        error instanceof CognitoError ? error.message : 'could not reach the sign-in service',
      );
    } finally {
      setWorking(false);
    }
  };

  return (
    <div class="signin">
      {/* The sprout-on-a-shelf mark, drawn in tokens so it themes itself. The wordmark drops
          the hyphen to match the domain; the app's name everywhere else keeps it. */}
      <h1>
        <svg viewBox="0 0 36 36" width="36" height="36" aria-hidden="true">
          <path
            d="M18 34 C 18 27, 16.5 22, 18 14"
            fill="none"
            stroke="var(--calm)"
            stroke-width="3"
            stroke-linecap="round"
          />
          <path d="M18 22 C 9 20, 4.5 14, 6 6.5 C 13.5 8, 18 13, 18 22 Z" fill="var(--calm)" />
          <path
            d="M18 18 C 25.5 16, 30 10.5, 29 4 C 22 5.5, 18 10, 18 18 Z"
            fill="var(--calm)"
            opacity="0.55"
          />
          <rect x="6" y="31" width="24" height="3" rx="1.5" fill="currentColor" opacity="0.9" />
        </svg>
        <span>
          shelf<span class="aware">aware</span>
        </span>
      </h1>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <label>
          Email
          <input
            type="email"
            value={email}
            autocomplete="username"
            required
            onInput={(event) => setEmail((event.target as HTMLInputElement).value)}
          />
        </label>

        <label>
          Password
          <input
            type="password"
            value={password}
            autocomplete="current-password"
            required
            onInput={(event) => setPassword((event.target as HTMLInputElement).value)}
          />
        </label>

        {refusal === null ? null : (
          <p class="refusal" role="alert">
            {refusal}
          </p>
        )}

        <button type="submit" class="primary" disabled={working}>
          {working ? 'Signing in…' : 'Sign in'}
        </button>
      </form>

      <p class="aside">
        Members are invited by hand — there is no sign-up. Ask whoever set up the household.
      </p>
    </div>
  );
}
